import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  GameStatus,
  ScoreStatus,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { MLBBall } from '@sorare/core/src/atoms/icons/MLBBall';
import { NBABall } from '@sorare/core/src/atoms/icons/NBABall';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  BrandXXS,
  LabelL,
  LabelM,
  LabelS,
  LabelXS,
} from '@sorare/core/src/atoms/typography';
import { CardAvatar } from '@sorare/core/src/components/card/Avatar';
import { Card } from '@sorare/core/src/components/card/Card';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { CardLockerOverlay } from '@sorare/core/src/components/card/CardLockerOverlay';
import { CardScore } from '@sorare/core/src/components/scoring/CardScore';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSendUserEngagementIncrement } from '@sorare/core/src/hooks/useSendUserEngagementIncrement';
import { isTotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import {
  cardAttributes,
  glossary,
  playerUnavailability,
} from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { statLabels } from '@sorare/core/src/lib/scoring';
import { UserEngagementScoreIncrement_Source } from '@sorare/core/src/protos/webview/channel/native_messages';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { ScheduledPlayerGameScoreState } from '@sorare/marketplace/src/components/game/ScheduledPlayerGameScoreState';
import { AnyPlayerStatsIcon } from '@sorare/marketplace/src/components/player/AnyPlayerStatsIcon';
import { PlayerDrawer } from '@sorare/marketplace/src/components/player/PlayerDrawer';
import { EmptyVaultPlaceholderMissionsBody } from '@sorare/marketplace/src/components/sealing/EmptyVaultPlaceholderMissionsBody';
import { PlayerRotowireStatsProjection } from '@sorare/marketplace/src/shared/player/PlayerDetails/PlayerStats/PlayerRotowireStatsProjection';
import { ProbablePitcher } from '@sorare/marketplace/src/shared/player/ProbablePitcher';

import { BasicBenchCard } from '#src/components/compose/BasicBenchCard/index.tsx';
import { Game } from '#src/components/game/Game/index.tsx';
import { OwnedCardFooter } from '#src/components/missions/TaskCompose/OwnedCardFooter/index.tsx';
import { TaskRules } from '#src/components/missions/TaskRules/index.tsx';
import { DecisivePickerSlot } from '#src/components/missions/picker/DecisivePickerSlot/index.tsx';
import { TaskCardsPicker } from '#src/components/missions/picker/TaskCardsPicker/index.tsx';
import { TaskPickerDialogLayout } from '#src/components/missions/picker/TaskPickerDialogLayout/index.tsx';
import { useUpsertTaskAppearances } from '#src/components/missions/picker/useUpsertTaskAppearances.tsx';

import { DecisivePlayerPickerOnboardingDialog } from './DecisivePlayerPickerOnboardingDialog/index.tsx';
import { NoCardState } from './NoCardState/index.tsx';
import { NoGameState } from './NoGameState/index.tsx';
import type {
  CardsPerGameQuery,
  CardsPerGameQueryVariables,
  DecisivePlayerPickerDialogQuery,
  DecisivePlayerPickerDialogQueryVariables,
  DecisivePlayerPickerDialog_taskAppearances,
} from './__generated__/index.graphql.ts';

const CardAvartarWrapper = styled.div`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
`;

const CountWrapper = styled(CardAvartarWrapper)`
  border: 1px solid var(--c-nd-150-opaque);
  background: var(--c-nd-50);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BlackOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: var(--c-nl-500);
`;

const Circle = styled.div`
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  left: var(--unit);
  top: var(--unit);
  background-color: var(--c-blue-400);
  align-content: center;

  @media ${tabletAndAbove} {
    left: var(--intermediate-unit);
    top: var(--intermediate-unit);
  }
`;

const CapitalizedLabelM = styled(LabelM)`
  text-transform: capitalize;
`;

const taskAppearancesFragments = gql`
  fragment DecisivePlayerPickerDialog_taskAppearances on DecisivePlayerPickerTask {
    id
    taskAppearances {
      id
      card {
        slug
        ...DecisivePickerSlot_card
      }
      ...DecisivePickerSlot_taskAppearance
    }
  }
  ${DecisivePickerSlot.fragments.taskAppearance}
  ${DecisivePickerSlot.fragments.card}
` as TypedDocumentNode<DecisivePlayerPickerDialog_taskAppearances>;

const DECISIVE_PLAYER_PICKER_QUERY = gql`
  query DecisivePlayerPickerDialogQuery($taskId: ID!, $after: String) {
    currentUser {
      slug
      task(id: $taskId) {
        id
        rarity
        ... on DecisivePlayerPickerTask {
          id
          sealed
          decisiveActions
          mode
          defaultAverageType
          maxAppearancesCount
          displayedTypedRules {
            id
            key
            ...TaskRules_so5RuleInterface
          }
          pickableGames(after: $after) {
            nodes {
              id
              game {
                id
                awayTeam {
                  slug
                  officialName
                  styleGuideColor
                }
                homeTeam {
                  slug
                  officialName
                }
                ...Game_anyGameInterface
                ...NoCardState_game
              }

              cards {
                slug
                ...DecisivePickerSlot_card
                ...CardAvatar_anyCard
              }
              totalCount
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
          ...DecisivePlayerPickerDialog_taskAppearances
        }
        ...TaskPickerDialogLayout_task
        ...NoCardState_task
      }
    }
  }
  ${CardAvatar.fragments.anyCard}
  ${Game.fragments.anyGameInterface}
  ${taskAppearancesFragments}
  ${DecisivePickerSlot.fragments.card}
  ${TaskPickerDialogLayout.fragments.task}
  ${NoCardState.fragments.game}
  ${NoCardState.fragments.task}
  ${TaskRules.fragments.so5RuleInterface}
` as TypedDocumentNode<
  DecisivePlayerPickerDialogQuery,
  DecisivePlayerPickerDialogQueryVariables
>;

const CARDS_PER_GAME_QUERY = gql`
  query CardsPerGameQuery(
    $gameId: ID!
    $taskId: ID!
    $after: String
    $so5LeaderboardSlug: String
  ) {
    currentUser {
      slug
      task(id: $taskId) {
        id
        ... on DecisivePlayerPickerTask {
          id
          sealed
          mode
          pickableCards(
            gameId: $gameId
            after: $after
            sorts: [POPULAR_FIRST, RARITIES]
          ) {
            nodes {
              slug
              averageDecisiveScore: averageScore(
                type: LAST_FIFTEEN_AVERAGE_DECISIVE_SCORE
              )
              anyPlayer {
                slug
                sport
                activeInjuries {
                  id
                  status
                }
                playerGameScore(gameId: $gameId) {
                  id
                  anyGame {
                    id
                    ... on GameOfBaseball {
                      id
                      probablePitchers {
                        slug
                      }
                    }
                  }
                  ...ScheduledPlayerGameScoreState_playerGameScore
                  ...PlayerRotowireStatsProjection_PlayerGameScoreInterface
                }
                ...AnyPlayerStatsIcon_anyPlayer
                ...PlayerRotowireStatsProjection_AnyPlayerInterface
              }
              usedIn(
                so5LeaderboardSlug: $so5LeaderboardSlug
                taskId: $taskId
                gameId: $gameId
              ) {
                slug
                ... on UsedInTasks {
                  slug
                  tasks {
                    id
                  }
                }
                ... on ListedOnMarket {
                  slug
                }
                ... on UsedInSo5Lineups {
                  slug
                  so5Lineups {
                    id
                  }
                }
              }
              ...DecisivePickerSlot_card
              ...Card_anyCard
              ...CardScore_anyCard
              ...OwnedCardFooter_anyCardInterface
              ...TaskCardsPicker_anyCardInterface
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    }
  }
  ${DecisivePickerSlot.fragments.card}
  ${Card.fragments.anyCard}
  ${AnyPlayerStatsIcon.fragments.anyPlayer}
  ${ScheduledPlayerGameScoreState.fragments.playerGameScore}
  ${CardScore.fragments.anyCard}
  ${OwnedCardFooter.fragments.anyCardInterface}
  ${TaskCardsPicker.fragments.anyCardInterface}
  ${PlayerRotowireStatsProjection.fragments.anyPlayerInterface}
  ${PlayerRotowireStatsProjection.fragments.playerGameScoreInterface}
` as TypedDocumentNode<CardsPerGameQuery, CardsPerGameQueryVariables>;

const normalizeTaskAppearances = (
  taskAppearances: DecisivePlayerPickerDialog_taskAppearances['taskAppearances'],
  slotNumber = 3
) => {
  return Array.from({ length: slotNumber }, (_, index) => {
    const status = taskAppearances.find(ta => ta.index === index)
      ?.playerGameScore?.anyGame?.statusTyped;
    return {
      isLocked:
        status &&
        [
          GameStatus.played,
          GameStatus.playing,
          GameStatus.suspended,
        ].includesValue(status),
      card: taskAppearances.find(ta => ta.index === index)?.card,
    };
  });
};

type PickableGame = NonNullable<
  Extract<
    NonNullable<DecisivePlayerPickerDialogQuery['currentUser']>['task'],
    { __typename: 'DecisivePlayerPickerTask' }
  >['pickableGames']['nodes'][number]['game']
>;

type PickableCard = NonNullable<
  Extract<
    NonNullable<CardsPerGameQuery['currentUser']>['task'],
    { __typename: 'DecisivePlayerPickerTask' }
  >['pickableCards']['nodes'][number]
>;

type Props = {
  taskId: string;
  defaultSelectedSlotIndex?: number;
  skipOnboarding?: boolean;
  onClose: () => void;
  onSuccess: () => void;
};
export const DecisivePlayerPickerDialog = ({
  taskId,
  defaultSelectedSlotIndex = 0,
  skipOnboarding = false,
  onClose,
  onSuccess,
}: Props) => {
  const { sport } = useSportContext();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { lifecycle, update } = useLifecycle();
  const [cardDetails, setCardDetails] = useState<PickableCard>();
  const [selectedGame, setSelectedGame] = useState<PickableGame>();
  const { sendUserEngagementIncrement } = useSendUserEngagementIncrement();

  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();
  const {
    data,
    loading: loadingGames,
    loadMore: loadMoreGames,
  } = usePaginatedQuery(DECISIVE_PLAYER_PICKER_QUERY, {
    variables: { taskId: idFromObject(taskId) },
    connection: 'GameWithCardsPreviewConnection',
  });

  const task = data?.currentUser?.task;

  const gamesPageInfo = isType(task, 'DecisivePlayerPickerTask')
    ? task.pickableGames.pageInfo
    : undefined;

  const { InfiniteScrollLoader: GamesInfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMoreGames(false, {
        after: gamesPageInfo?.endCursor,
      });
    },
    Boolean(gamesPageInfo?.hasNextPage),
    loadingGames
  );

  const {
    data: cardsPerGameData,
    loading: loadingCards,
    loadMore,
  } = usePaginatedQuery(CARDS_PER_GAME_QUERY, {
    variables: {
      taskId: idFromObject(taskId),
      gameId: idFromObject(selectedGame?.id) || '',
    },
    connection: 'AnyCardInterfaceConnection',
    skip: !selectedGame?.id,
  });

  const cardsPageInfo = isType(
    cardsPerGameData?.currentUser?.task,
    'DecisivePlayerPickerTask'
  )
    ? cardsPerGameData?.currentUser?.task?.pickableCards.pageInfo
    : undefined;

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: cardsPageInfo?.endCursor,
      });
    },
    Boolean(cardsPageInfo?.hasNextPage),
    loadingCards
  );

  const [upsertTaskAppearances, { loading }] = useUpsertTaskAppearances();

  // TODO: Improve with a loader
  if (!task || !isType(task, 'DecisivePlayerPickerTask')) {
    return null;
  }
  const slotNumber = task?.maxAppearancesCount ?? 3;

  const pickableGames = task.pickableGames.nodes;

  if (pickableGames.length > 0 && !selectedGame) {
    setSelectedGame(pickableGames[0].game);
  }

  const defaultSlots = normalizeTaskAppearances(
    task.taskAppearances,
    slotNumber
  );

  const onConfirm = async (
    selectedCardSlugs: { slug?: string; index: number }[]
  ) => {
    const { errors } = await upsertTaskAppearances({
      variables: {
        input: {
          taskId: task.id,
          taskAppearances: selectedCardSlugs.map(({ slug, index }) => ({
            index,
            cardSlug: slug || undefined,
          })),
        },
      },
    });

    if (errors.length) {
      return;
    }

    sendUserEngagementIncrement(
      3,
      UserEngagementScoreIncrement_Source.PICK_PLAYER_IN_DAILY_MISSION
    );

    onSuccess();
  };

  const isVaultMission = !!task.sealed;
  const shouldShowOnboarding = isVaultMission
    ? !lifecycle?.sawVaultDecisivePlayerPickerOnboarding
    : !lifecycle?.sawDecisivePlayerPickerOnboarding;

  if (shouldShowOnboarding && !skipOnboarding) {
    return (
      <DecisivePlayerPickerOnboardingDialog
        isVaultMission={isVaultMission}
        onClose={() => {
          update(
            isVaultMission
              ? LIFECYCLE.sawVaultDecisivePlayerPickerOnboarding
              : LIFECYCLE.sawDecisivePlayerPickerOnboarding,
            true
          );
        }}
      />
    );
  }

  const cardsPerGame = isType(
    cardsPerGameData?.currentUser?.task,
    'DecisivePlayerPickerTask'
  )
    ? cardsPerGameData?.currentUser?.task?.pickableCards.nodes
    : [];

  return (
    <TaskPickerDialogLayout
      task={task}
      onClose={onClose}
      leftColumnChildren={
        <Vertical gap={3}>
          {task.displayedTypedRules.length > 0 && (
            <TaskRules displayedTypedRules={task.displayedTypedRules || []} />
          )}
          {task.mode === 'DECISIVE' && (
            <Vertical gap={1.5}>
              <LabelL bold>
                <FormattedMessage
                  id="DecisivePlayerPickerDialog.decisiveActions"
                  defaultMessage="Decisive Actions"
                />
              </LabelL>
              <Vertical>
                {task.decisiveActions.length > 0 ? (
                  task.decisiveActions.map(action => (
                    <Horizontal key={action}>
                      <StatIcon
                        statKey={
                          action as ComponentProps<typeof StatIcon>['statKey']
                        }
                      />
                      <CapitalizedLabelM>
                        <FormattedMessage
                          {...statLabels[action as keyof typeof statLabels]}
                        />
                      </CapitalizedLabelM>
                    </Horizontal>
                  ))
                ) : (
                  <LabelM>
                    <FormattedMessage
                      id="DecisivePlayerPickerDialog.everyDecisiveActionCounts"
                      defaultMessage="Every decisive action counts"
                    />
                  </LabelM>
                )}
              </Vertical>
            </Vertical>
          )}
          <LabelL bold>
            {sport === Sport.FOOTBALL ? (
              <FormattedMessage
                id="missions.daily.myMatches"
                defaultMessage="My Matches"
              />
            ) : (
              <FormattedMessage
                id="missions.daily.myGames"
                defaultMessage="My Games"
              />
            )}
          </LabelL>
          {pickableGames.length > 0 ? (
            <ConditionalWrapper
              wrap={!isTabletAndAbove}
              Wrapper={Scrollable}
              props={{
                itemToDisplay: 1,
                itemGap: 2,
                overhang: 4,
              }}
            >
              {pickableGames.map(pg => {
                return (
                  <Game
                    key={pg.game.id}
                    game={pg.game}
                    onClick={() => {
                      setSelectedGame(pg.game);
                    }}
                    selected={selectedGame?.id === pg.game.id}
                  >
                    {pg.totalCount > 0 && (
                      <Horizontal center>
                        {pg.cards.map(card => (
                          <CardAvartarWrapper key={card.slug}>
                            <CardAvatar
                              className="rounded"
                              maxWidth={40}
                              card={card}
                            />
                          </CardAvartarWrapper>
                        ))}
                        {pg.totalCount > 5 && (
                          <CountWrapper>
                            <LabelXS bold>+{pg.totalCount - 5}</LabelXS>
                          </CountWrapper>
                        )}
                      </Horizontal>
                    )}
                  </Game>
                );
              })}
              <GamesInfiniteScrollLoader />
            </ConditionalWrapper>
          ) : (
            <NoGameState tasksExpiresAt={dailyTasksExpiresAt} />
          )}
        </Vertical>
      }
    >
      <TaskCardsPicker
        title={
          selectedGame
            ? `${selectedGame?.homeTeam?.officialName} vs ${selectedGame?.awayTeam?.officialName}`
            : ''
        }
        defaultSlots={defaultSlots}
        clickToUnselect
        defaultSelectedSlotIndex={defaultSelectedSlotIndex}
        cards={cardsPerGame || []}
        taskId={taskId}
        renderButton={slots => {
          return (
            <LoadingButton
              size="medium"
              loading={loading}
              onClick={() => {
                onConfirm(slots);
              }}
            >
              <FormattedMessage {...glossary.confirm} />
            </LoadingButton>
          );
        }}
        renderCard={({ card, cardSlotIndex }) => {
          const isLocked =
            (isType(card.usedIn, 'UsedInTasks') &&
              card.usedIn.tasks.filter(tsk => tsk.id !== taskId).length > 0) ||
            (isType(card.usedIn, 'UsedInSo5Lineups') &&
              card.usedIn.so5Lineups.length > 0) ||
            isType(card.usedIn, 'ListedOnMarket');
          const { playerGameScore } = card.anyPlayer;

          const [injury] = card.anyPlayer.activeInjuries;
          const isOut = injury && ['OFS', 'OUT'].includes(injury.status!);
          const isProbablePitcher =
            playerGameScore &&
            isType(playerGameScore.anyGame, 'GameOfBaseball') &&
            playerGameScore.anyGame.probablePitchers.some(
              pp => pp.slug === card.anyPlayer.slug
            );

          return (
            <BasicBenchCard
              maskImageSrc={card.pictureUrl}
              cornerBadge={
                <>
                  {!!playerGameScore && (
                    <ScheduledPlayerGameScoreState
                      playerGameScores={[playerGameScore]}
                      variant="cornerBadge"
                    />
                  )}
                  {card.anyPlayer.sport !== Sport.FOOTBALL && (
                    <PlayerRotowireStatsProjection
                      variant="cornerBadge"
                      player={card.anyPlayer}
                      positions={card.anyPositions}
                      playerGameScores={[playerGameScore]}
                    />
                  )}
                  {!!isProbablePitcher && (
                    <ProbablePitcher variant="cornerBadge" />
                  )}
                </>
              }
              footer={
                <CardFooter
                  left={
                    <AnyPlayerStatsIcon
                      backgroundColor="transparent"
                      player={card.anyPlayer}
                      onClick={e => {
                        e?.stopPropagation();
                        setCardDetails(card);
                      }}
                    />
                  }
                  right={
                    card.anyPlayer.sport === Sport.FOOTBALL ? (
                      <Tooltip
                        title={
                          <FormattedMessage
                            {...cardAttributes.L15DecisiveScore}
                          />
                        }
                      >
                        <Horizontal gap={0.5}>
                          <LabelXS>
                            <FormattedMessage
                              id="BasicBenchCard.desiciveScoreLabel.short"
                              defaultMessage="DS"
                            />
                            :
                          </LabelXS>
                          <LabelS>
                            <Score
                              hideReviewing
                              shape="TEXT"
                              score={card.averageDecisiveScore || 0}
                              scoreType="PLAYER_SCORE"
                              scoreStatus={ScoreStatus.FINAL}
                            />
                          </LabelS>
                        </Horizontal>
                      </Tooltip>
                    ) : (
                      <>
                        {injury ? (
                          <Tooltip
                            title={
                              <LabelXS>
                                <FormattedMessage
                                  {...playerUnavailability.injuryTitle}
                                />
                              </LabelXS>
                            }
                          >
                            <Horizontal gap={0.5}>
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                color={
                                  isOut
                                    ? 'var(--c-red-600)'
                                    : 'var(--c-yellow-400)'
                                }
                                width={12}
                              />
                              <LabelXS>{injury.status}</LabelXS>
                            </Horizontal>
                          </Tooltip>
                        ) : (
                          <LabelXS>
                            {card.anyPlayer.sport === Sport.NBA ? (
                              <NBABall
                                color="var(--c-onBase-success)"
                                size={3}
                              />
                            ) : (
                              <MLBBall
                                color="var(--c-onBase-success)"
                                size={2}
                              />
                            )}
                          </LabelXS>
                        )}
                      </>
                    )
                  }
                  extra={
                    <OwnedCardFooter
                      card={card}
                      taskId={task.id}
                      showOwnedLabel={false}
                    />
                  }
                >
                  <CardScore
                    card={card}
                    mode={
                      isTotalAveragePlayerScore(task.defaultAverageType)
                        ? task.defaultAverageType
                        : AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
                    }
                    shape="HEXAGON"
                  />
                </CardFooter>
              }
            >
              <Card card={card} />
              {cardSlotIndex !== -1 && (
                <BlackOverlay>
                  <Circle>
                    <BrandXXS>{cardSlotIndex + 1}</BrandXXS>
                  </Circle>
                </BlackOverlay>
              )}
              <CardLockerOverlay active={isLocked} />
            </BasicBenchCard>
          );
        }}
        renderSlotContent={({ isSelected, card, index, isLocked }) => {
          const cardTaskAppearance =
            card && task.taskAppearances.find(t => t.card?.slug === card.slug);

          return (
            <DecisivePickerSlot
              card={card}
              taskAppearance={cardTaskAppearance}
              isSelected={isSelected}
              showLockedOverlay={isLocked}
              index={index}
              mode={task.mode}
            />
          );
        }}
      >
        {!!selectedGame && cardsPerGame.length === 0 && !loadingCards && (
          <>
            {isVaultMission ? (
              <EmptyVaultPlaceholderMissionsBody />
            ) : (
              <NoCardState game={selectedGame} task={task} />
            )}
          </>
        )}

        <InfiniteScrollLoader />
      </TaskCardsPicker>
      {!!cardDetails && (
        <PlayerDrawer
          cardOpened={{
            playerSlug: cardDetails.anyPlayer?.slug || '',
            cardSlug: cardDetails.slug || '',
          }}
          onClose={() => {
            setCardDetails(undefined);
          }}
        />
      )}
    </TaskPickerDialogLayout>
  );
};
